import { LastPsychologist } from "./interfaces"
import { isStorageAvailable, safeSetItem } from "./infrastructure/storage-util"

const SIGNED_IN_KEY = "alter_signed_in"

interface UserAccountData {
    name?: string
    email: string
    phone?: string
    utm: string
    source?: string
    ga_client_id: string | undefined
    b2b: boolean
    email_readonly: boolean
    company_inn?: string | null
    company_ooo?: string | null
    company_ogrn?: string | null
    company_address?: string | null
    psychologists?: LastPsychologist[]
}

class _UserAccount {
    storage: Storage | undefined

    constructor() {
        this.storage = isStorageAvailable ? window.localStorage : undefined
    }

    onSignUpQueue: Array<(data?: UserAccountData) => void> = []
    signOutQueue: Array<() => void> = []

    get isSignedIn() {
        return this.email || false
    }

    get data() {
        if (!this.storage) return undefined

        const data = this.storage?.getItem(SIGNED_IN_KEY)
        if (!data) return undefined
        return JSON.parse(data) as UserAccountData
    }

    get email() {
        return this.data ? this.data.email : undefined
    }

    get utm() {
        return this.data ? this.data.utm : undefined
    }

    get name() {
        return this.data ? this.data.name : undefined
    }

    get phone() {
        return this.data ? this.data.phone : undefined
    }

    get b2b() {
        return this.data ? this.data.b2b : false
    }

    executeOnceWhenSignedIn(handler: () => void) {
        this.onSignUpQueue.push(handler)
    }

    runSignUpQueue() {
        for (const handler of this.onSignUpQueue) {
            handler(this.data)
        }
        this.onSignUpQueue = []
    }

    storeSignIn(data: Partial<UserAccountData>) {
        if (!this.storage) return

        const dataToSave = Object.assign({}, data, {
            timestamp: new Date().toISOString(),
        })
        const cleanup = (key: string, value: unknown) => value ?? undefined
        const item = JSON.stringify(dataToSave, cleanup)

        safeSetItem(this.storage, SIGNED_IN_KEY, item)
    }

    signOut() {
        if (!this.storage) return
        this.storage?.removeItem(SIGNED_IN_KEY)
        this.signOutQueue.forEach((handler) => handler())
    }

    withUserData(json: object): object & { user_account: UserAccountData | undefined } {
        return {
            ...json,
            user_account: this.data,
        }
    }
}

export const UserAccount = new _UserAccount()
